import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRAVA, type Pravo } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Role, Uzivatel } from '../api/types.js';
import { PRAVO_LABEL } from '../lib/labels.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  Badge,
  Btn,
  Chyba,
  EmptyState,
  Input,
  Karta,
  Nacitani,
  Napoveda,
  Pole,
  td,
  th,
} from '../components/ui.js';

/** Uživatelé + role (jen právo admin.users) — jednoduché tabulky s formuláři. */
export function UzivatelePage() {
  const { maPravo } = useAuth();
  return (
    <>
      <h1 className="text-2xl font-bold">Uživatelé a role</h1>
      {maPravo('admin.users') ? (
        <>
          <UzivateleSekce />
          <RoleSekce />
        </>
      ) : (
        <EmptyState
          titulek="Na správu uživatelů nemáte oprávnění"
          popis="Účty a role spravuje správce firmy. Požádejte ho, pokud potřebujete přidat člověka nebo změnit práva."
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Uživatelé
// ---------------------------------------------------------------------------

function UzivateleSekce() {
  const queryClient = useQueryClient();
  const [formular, setFormular] = useState<Uzivatel | 'novy' | null>(null);

  const {
    data: uzivatele,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['uzivatele'],
    queryFn: () => api.get<Uzivatel[]>('/api/users'),
  });

  const deaktivovat = useMutation({
    mutationFn: (id: string) => api.del<Uzivatel>(`/api/users/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['uzivatele'] }),
  });

  return (
    <Karta
      nadpis="Uživatelé"
      akce={
        formular === null ? <Btn onClick={() => setFormular('novy')}>Nový uživatel</Btn> : undefined
      }
    >
      {formular !== null && (
        <UzivatelForm
          uzivatel={formular === 'novy' ? null : formular}
          onHotovo={() => setFormular(null)}
        />
      )}
      {isLoading && <Nacitani />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {deaktivovat.error ? <Chyba>{hlaskaZChyby(deaktivovat.error)}</Chyba> : null}
      {uzivatele && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={th}>Jméno</th>
                <th className={th}>E-mail</th>
                <th className={th}>Role</th>
                <th className={th}>Stav</th>
                <th className={th}>Akce</th>
              </tr>
            </thead>
            <tbody>
              {uzivatele.map((u) => (
                <tr key={u.id} className={u.aktivni ? '' : 'text-text-secondary'}>
                  <td className={`${td} font-semibold`}>{u.jmeno}</td>
                  <td className={td}>{u.email}</td>
                  <td className={td}>{u.roles.map((r) => r.nazev).join(', ') || '—'}</td>
                  <td className={td}>
                    <Badge stav={u.aktivni ? 'success' : 'neutral'}>
                      {u.aktivni ? 'aktivní' : 'deaktivovaný'}
                    </Badge>
                  </td>
                  <td className={td}>
                    <div className="flex gap-2">
                      <Btn varianta="secondary" onClick={() => setFormular(u)}>
                        Upravit
                      </Btn>
                      {u.aktivni && (
                        <Btn
                          varianta="danger"
                          disabled={deaktivovat.isPending}
                          onClick={() => deaktivovat.mutate(u.id)}
                        >
                          Deaktivovat
                        </Btn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karta>
  );
}

function UzivatelForm({ uzivatel, onHotovo }: { uzivatel: Uzivatel | null; onHotovo: () => void }) {
  const queryClient = useQueryClient();
  const [jmeno, setJmeno] = useState(uzivatel?.jmeno ?? '');
  const [email, setEmail] = useState(uzivatel?.email ?? '');
  const [heslo, setHeslo] = useState('');
  const [telefon, setTelefon] = useState(uzivatel?.telefon ?? '');
  const [roleIds, setRoleIds] = useState<string[]>(uzivatel?.roles.map((r) => r.id) ?? []);

  const { data: role } = useQuery({
    queryKey: ['role'],
    queryFn: () => api.get<Role[]>('/api/roles'),
  });

  const ulozit = useMutation({
    mutationFn: () => {
      const data = {
        jmeno,
        email,
        telefon: telefon || null,
        roleIds,
        ...(heslo !== '' ? { heslo } : {}),
      };
      return uzivatel
        ? api.patch<Uzivatel>(`/api/users/${uzivatel.id}`, data)
        : api.post<Uzivatel>('/api/users', data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['uzivatele'] });
      void queryClient.invalidateQueries({ queryKey: ['role'] });
      onHotovo();
    },
  });

  const odeslat = (e: FormEvent) => {
    e.preventDefault();
    ulozit.mutate();
  };

  return (
    <form
      onSubmit={odeslat}
      className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-bg p-4"
    >
      <h3 className="text-lg font-bold">
        {uzivatel ? `Úprava — ${uzivatel.jmeno}` : 'Nový uživatel'}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <Pole popisek="Jméno" povinne>
          <Input required value={jmeno} onChange={(e) => setJmeno(e.target.value)} />
        </Pole>
        <Pole popisek="E-mail" povinne>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Pole>
        <Pole popisek={uzivatel ? 'Nové heslo (nepovinné)' : 'Heslo (min. 8 znaků)'}>
          <Input
            type="password"
            autoComplete="new-password"
            minLength={heslo === '' ? undefined : 8}
            value={heslo}
            onChange={(e) => setHeslo(e.target.value)}
          />
        </Pole>
        <Pole popisek="Telefon">
          <Input value={telefon ?? ''} onChange={(e) => setTelefon(e.target.value)} />
        </Pole>
      </div>
      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-text-secondary">Role</legend>
        <div className="flex flex-wrap gap-4">
          {role?.map((r) => (
            <label key={r.id} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                className="size-5"
                checked={roleIds.includes(r.id)}
                onChange={(e) =>
                  setRoleIds((stare) =>
                    e.target.checked ? [...stare, r.id] : stare.filter((id) => id !== r.id),
                  )
                }
              />
              {r.nazev}
            </label>
          ))}
        </div>
      </fieldset>
      {ulozit.error ? <Chyba>{hlaskaZChyby(ulozit.error)}</Chyba> : null}
      <div className="flex gap-3">
        <Btn type="submit" disabled={ulozit.isPending}>
          {ulozit.isPending ? 'Ukládám…' : 'Uložit uživatele'}
        </Btn>
        <Btn varianta="secondary" onClick={onHotovo}>
          Zrušit
        </Btn>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Role
// ---------------------------------------------------------------------------

function RoleSekce() {
  const queryClient = useQueryClient();
  const [formular, setFormular] = useState<Role | 'nova' | null>(null);

  const {
    data: role,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['role'],
    queryFn: () => api.get<Role[]>('/api/roles'),
  });

  const smazat = useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/api/roles/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['role'] }),
  });

  return (
    <Karta
      nadpis="Role"
      akce={
        formular === null ? <Btn onClick={() => setFormular('nova')}>Nová role</Btn> : undefined
      }
    >
      {formular !== null && (
        <RoleForm role={formular === 'nova' ? null : formular} onHotovo={() => setFormular(null)} />
      )}
      {isLoading && <Nacitani />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {smazat.error ? <Chyba>{hlaskaZChyby(smazat.error)}</Chyba> : null}
      {role && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={th}>Název</th>
                <th className={th}>Práva</th>
                <th className={th}>Uživatelů</th>
                <th className={th}>Akce</th>
              </tr>
            </thead>
            <tbody>
              {role.map((r) => (
                <tr key={r.id}>
                  <td className={`${td} font-semibold`}>{r.nazev}</td>
                  <td className={`${td} text-sm`}>
                    {r.prava.length > 0
                      ? r.prava.map((p) => PRAVO_LABEL[p as Pravo] ?? p).join(', ')
                      : '—'}
                  </td>
                  <td className={td}>{r.users?.length ?? 0}</td>
                  <td className={td}>
                    <div className="flex gap-2">
                      <Btn varianta="secondary" onClick={() => setFormular(r)}>
                        Upravit
                      </Btn>
                      <Btn
                        varianta="danger"
                        disabled={smazat.isPending}
                        onClick={() => smazat.mutate(r.id)}
                      >
                        Smazat
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karta>
  );
}

function RoleForm({ role, onHotovo }: { role: Role | null; onHotovo: () => void }) {
  const queryClient = useQueryClient();
  const [nazev, setNazev] = useState(role?.nazev ?? '');
  const [popis, setPopis] = useState(role?.popis ?? '');
  const [prava, setPrava] = useState<Pravo[]>((role?.prava as Pravo[]) ?? []);

  const ulozit = useMutation({
    mutationFn: () => {
      const data = { nazev, popis: popis || null, prava };
      return role
        ? api.patch<Role>(`/api/roles/${role.id}`, data)
        : api.post<Role>('/api/roles', data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['role'] });
      onHotovo();
    },
  });

  const odeslat = (e: FormEvent) => {
    e.preventDefault();
    ulozit.mutate();
  };

  return (
    <form
      onSubmit={odeslat}
      className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-bg p-4"
    >
      <h3 className="text-lg font-bold">{role ? `Úprava — ${role.nazev}` : 'Nová role'}</h3>
      <div className="grid grid-cols-2 gap-4">
        <Pole popisek="Název role" povinne>
          <Input required value={nazev} onChange={(e) => setNazev(e.target.value)} />
        </Pole>
        <Pole popisek="Popis">
          <Input value={popis ?? ''} onChange={(e) => setPopis(e.target.value)} />
        </Pole>
      </div>
      <fieldset>
        <legend className="mb-2 flex items-center gap-2 text-sm font-semibold text-text-secondary">
          Práva
          {/* Dlouhé vysvětlení „zdi financí" už ne natrvalo v legendě — za „?"
              (blueprint 16 §11). */}
          <Napoveda titulek="Jak fungují práva">
            <p>
              Zeď kolem financí drží server — bez práva „finance — čtení" API finanční pole vůbec
              neposílá. Právo tedy nejen skryje, ale i zabrání načtení citlivých čísel.
            </p>
          </Napoveda>
        </legend>
        <div className="grid grid-cols-3 gap-2">
          {PRAVA.map((p) => (
            <label key={p} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                className="size-5"
                checked={prava.includes(p)}
                onChange={(e) =>
                  setPrava((stara) =>
                    e.target.checked ? [...stara, p] : stara.filter((x) => x !== p),
                  )
                }
              />
              {PRAVO_LABEL[p]}
            </label>
          ))}
        </div>
      </fieldset>
      {ulozit.error ? <Chyba>{hlaskaZChyby(ulozit.error)}</Chyba> : null}
      <div className="flex gap-3">
        <Btn type="submit" disabled={ulozit.isPending}>
          {ulozit.isPending ? 'Ukládám…' : 'Uložit roli'}
        </Btn>
        <Btn varianta="secondary" onClick={onHotovo}>
          Zrušit
        </Btn>
      </div>
    </form>
  );
}
